import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import api from '../utils/api';
import { isUserLoggedIn, getUserInfo } from '../utils/auth';
import { showSuccess, showError } from '../utils/toast';
import { trackPhoneView } from '../utils/phoneLogTracker';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const FATEHABAD_AREAS = [
  'Fatehabad', 'Ratia', 'Bhattu Kalan', 'Jakhal', 'Tohana',
  'Bhuna', 'Gorakhpur', 'Kulan', 'Tigrana', 'Pili Mandori',
  'Ludesar', 'Dhansu', 'Nehla', 'Bhirdana', 'Dhani Phogat', 'Other'
];

const BLOOD_GROUP_COLORS = {
  'A+':  { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  'A-':  { bg: '#fecaca', text: '#991b1b', border: '#f87171' },
  'B+':  { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  'B-':  { bg: '#fde68a', text: '#78350f', border: '#f59e0b' },
  'AB+': { bg: '#ede9fe', text: '#5b21b6', border: '#c4b5fd' },
  'AB-': { bg: '#ddd6fe', text: '#4c1d95', border: '#a78bfa' },
  'O+':  { bg: '#dcfce7', text: '#14532d', border: '#86efac' },
  'O-':  { bg: '#bbf7d0', text: '#166534', border: '#4ade80' },
};

const BloodDonors = () => {
  const navigate = useNavigate();
  const loggedIn = isUserLoggedIn();
  const userInfo = getUserInfo();

  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoggedInServer, setIsLoggedInServer] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // My registration state
  const [myDonor, setMyDonor] = useState(null);
  const [myDonorLoading, setMyDonorLoading] = useState(false);

  // Register modal
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    bloodGroup: '',
    phone: '',
    area: '',
    image: '',
    isAvailable: true
  });
  const [submitting, setSubmitting] = useState(false);

  const handleImageUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showError('Please select an image file (PNG, JPG, WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError('Image size should be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_SIZE = 400;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > MAX_SIZE) {
            height = Math.round((height * MAX_SIZE) / width);
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width = Math.round((width * MAX_SIZE) / height);
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setFormData(prev => ({ ...prev, image: dataUrl }));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleShareWhatsApp = (donor) => {
    if (!donor) return;
    const shareText = `*गर्व से कहो — "मैं भी रक्तवीर!"*\n\nमैंने Fatehabad में आपातकालीन रक्तदान के लिए खुद को पंजीकृत किया है।\n\n*रक्तवीर:* ${donor.name}\n*ब्लड ग्रुप:* ${donor.bloodGroup}\n*क्षेत्र:* ${donor.area}, फतेहाबाद\n\nअगर किसी को रक्त की आवश्यकता हो तो संपर्क करें या आप भी रक्तवीर बनकर जीवन बचाएं:\nhttps://digitalfatehabad.com/blood-donors`;
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };

  const fetchDonors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: 20 });
      if (selectedGroup) params.set('bloodGroup', selectedGroup);
      if (selectedArea) params.set('area', selectedArea);

      const res = await api.get(`/blood-donors?${params}`);
      setDonors(res.data.donors || []);
      setPagination(res.data.pagination);
      setIsLoggedInServer(res.data.isLoggedIn);
    } catch {
      setDonors([]);
    } finally {
      setLoading(false);
    }
  }, [selectedGroup, selectedArea, currentPage]);

  const fetchMyDonor = useCallback(async () => {
    if (!loggedIn) return;
    setMyDonorLoading(true);
    try {
      const res = await api.get('/blood-donors/my');
      setMyDonor(res.data);
    } catch {
      setMyDonor(null);
    } finally {
      setMyDonorLoading(false);
    }
  }, [loggedIn]);

  useEffect(() => { fetchDonors(); }, [fetchDonors]);
  useEffect(() => { fetchMyDonor(); }, [fetchMyDonor]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openRegisterModal = () => {
    if (!loggedIn) {
      navigate('/login', { state: { from: '/blood-donors' } });
      return;
    }
    if (myDonor) {
      // Edit mode
      setFormData({
        name: myDonor.name,
        bloodGroup: myDonor.bloodGroup,
        phone: myDonor.phone,
        area: myDonor.area,
        image: myDonor.image || '',
        isAvailable: myDonor.isAvailable
      });
      setEditMode(true);
    } else {
      setFormData({
        name: userInfo?.name || '',
        bloodGroup: '',
        phone: '',
        area: '',
        image: userInfo?.avatar || '',
        isAvailable: true
      });
      setEditMode(false);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editMode && myDonor) {
        await api.put(`/blood-donors/${myDonor._id}`, formData);
        showSuccess('Donor details updated!');
      } else {
        await api.post('/blood-donors', formData);
        showSuccess('You are now registered as a blood donor!');
      }
      setShowModal(false);
      fetchMyDonor();
      fetchDonors();
    } catch (err) {
      showError(err.response?.data?.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!myDonor) return;
    if (!window.confirm('Are you sure you want to remove yourself from the donor list?')) return;
    try {
      await api.delete(`/blood-donors/${myDonor._id}`);
      showSuccess('Removed from donor list.');
      setMyDonor(null);
      fetchDonors();
    } catch {
      showError('Could not remove. Try again.');
    }
  };

  const handleGroupFilter = (group) => {
    setSelectedGroup(prev => prev === group ? '' : group);
    setCurrentPage(1);
  };

  const displayedDonors = donors.filter(d => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();

    // 1. Direct match on name, area, or blood group
    if (d.name?.toLowerCase().includes(q) || d.area?.toLowerCase().includes(q) || d.bloodGroup?.toLowerCase() === q) {
      return true;
    }

    // 2. Strip generic filler words (blood, donor, donors, in, fatehabad, district, etc.)
    const cleanQ = q.replace(/\b(blood|donor|donors|in|district|verified|rakt|khoon)\b/gi, '').trim();

    // If only generic words like "blood donors" were typed, show all donors
    if (!cleanQ) {
      return true;
    }

    // If "fatehabad" was typed, show all since all donors are in Fatehabad district
    if (cleanQ === 'fatehabad') {
      return true;
    }

    // Split remaining terms and match against name, area, or blood group
    const terms = cleanQ.split(/\s+/).filter(Boolean);
    return terms.some(term =>
      d.name?.toLowerCase().includes(term) ||
      d.area?.toLowerCase().includes(term) ||
      d.bloodGroup?.toLowerCase().includes(term)
    );
  });

  const totalDonors = pagination?.total || donors.length;

  return (
    <>
      <SEO
        title="Blood Donors in Fatehabad District | Digital Fatehabad"
        description="Find blood donors in Fatehabad, Ratia, Tohana, Jakhal and nearby areas. Search by blood group — A+, A-, B+, B-, AB+, AB-, O+, O-. Register as a donor and save lives."
        canonical="/blood-donors"
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">

        {/* ── Hero ── */}
        <div className="relative overflow-hidden bg-gradient-to-br from-red-600 via-red-700 to-rose-800 text-white">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M30 0c0 16.569-13.431 30-30 30C0 16.569 13.431 0 30 0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
          <div className="relative container-custom py-6 sm:py-10 md:py-14 text-center px-4">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur rounded-2xl mb-3 shadow-inner">
              <span className="text-2xl sm:text-3xl">🩸</span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-2 tracking-tight">
              Blood Donors in Fatehabad
            </h1>
            <p className="text-red-100 text-xs sm:text-sm md:text-base mb-4 max-w-xl mx-auto px-2">
              Verified blood donors across Fatehabad district. Every donation can save up to 3 lives.
            </p>
            <div className="flex flex-col items-center justify-center gap-2 max-w-md mx-auto">
              <button
                onClick={openRegisterModal}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-red-700 font-bold rounded-xl hover:bg-red-50 transition-all shadow-lg active:scale-95 text-sm"
              >
                <span>🩸</span> {myDonor ? 'Update My Donor Profile' : 'Register as Blood Donor'}
              </button>
              {myDonor && (
                <button
                  onClick={handleRemove}
                  className="text-[11px] text-red-200/75 hover:text-white underline transition-colors pt-1"
                >
                  ✕ Remove me from donor list
                </button>
              )}
            </div>
            {!loggedIn && (
              <p className="mt-3 text-xs sm:text-sm text-red-200">
                <Link to="/login" state={{ from: '/blood-donors' }} className="underline font-bold text-white hover:text-red-100">Login</Link> to view contact numbers & call donors directly
              </p>
            )}
          </div>
        </div>

        <div className="container-custom py-4 sm:py-8 px-4">

          {/* ── "मैं भी रक्तवीर" Digital Certificate Card (Top) ── */}
          {myDonor ? (
            <div className="mb-6 relative overflow-hidden bg-gradient-to-br from-amber-50/90 via-white to-red-50/80 dark:from-gray-900 dark:via-gray-900 dark:to-red-950/40 rounded-3xl border-2 border-red-500/30 dark:border-red-500/40 shadow-xl p-5 sm:p-7">
              {/* Decorative Certificate Background watermark */}
              <div className="absolute -right-6 -bottom-6 text-9xl opacity-5 pointer-events-none select-none">
                🩸
              </div>
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red-600 via-amber-500 to-rose-600" />

              <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-5 relative z-10">
                {/* Left: Avatar + Details */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-4 flex-1 w-full">
                  {/* Donor Photo / Avatar with Blood Badge */}
                  <div className="relative flex-shrink-0">
                    {myDonor.image ? (
                      <img
                        src={myDonor.image}
                        alt={myDonor.name}
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-4 border-white dark:border-gray-800 shadow-md ring-2 ring-red-400"
                      />
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-red-500 to-rose-700 text-white flex items-center justify-center font-black text-2xl sm:text-3xl border-4 border-white dark:border-gray-800 shadow-md ring-2 ring-red-400">
                        {myDonor.name ? myDonor.name.charAt(0).toUpperCase() : '🩸'}
                      </div>
                    )}
                    <span className="absolute -bottom-2 -right-2 px-2.5 py-0.5 rounded-full text-xs font-black bg-red-600 text-white shadow-md border-2 border-white dark:border-gray-900">
                      {myDonor.bloodGroup}
                    </span>
                  </div>

                  {/* Certificate Text & Titles */}
                  <div className="flex-1 min-w-0">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300 dark:border-amber-700 mb-1.5 shadow-sm">
                      <span>🏆</span>
                      <span>Digital Fatehabad प्रमाणित रक्तदाता</span>
                    </div>

                    <h2 className="text-2xl sm:text-3xl font-black text-red-600 dark:text-red-500 tracking-tight">
                      मैं भी रक्तवीर! 🩸
                    </h2>

                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mt-0.5">
                      {myDonor.name}
                    </h3>

                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1 mt-1 text-xs text-gray-600 dark:text-gray-300 font-medium">
                      <span>📍 {myDonor.area}, फतेहाबाद</span>
                      <span>•</span>
                      <span className="text-green-600 dark:text-green-400 font-bold">
                        {myDonor.isAvailable ? '✅ Ready to Donate' : '⛔ Unavailable'}
                      </span>
                    </div>

                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                      "रक्तदान महादान — एक रक्तदान बचाए तीन जान"
                    </p>
                  </div>
                </div>

                {/* Right / Actions */}
                <div className="flex flex-col sm:flex-row md:flex-col gap-2 w-full sm:w-auto md:w-56 flex-shrink-0">
                  {/* Share this card on WhatsApp */}
                  <button
                    onClick={() => handleShareWhatsApp(myDonor)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-bold rounded-xl text-xs sm:text-sm shadow-md hover:shadow-lg transition-all active:scale-95"
                  >
                    <span className="text-base">💬</span>
                    <span>Share Card on WhatsApp</span>
                  </button>

                  <button
                    onClick={openRegisterModal}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold rounded-xl text-xs transition-all active:scale-95 shadow-sm"
                  >
                    <span>✏️</span>
                    <span>Edit Photo & Details</span>
                  </button>

                  <button
                    onClick={handleRemove}
                    className="text-[11px] text-gray-400 hover:text-red-500 underline text-center transition-colors pt-1"
                  >
                    Remove my registration
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-6 bg-gradient-to-r from-red-50 to-rose-100 dark:from-red-950/30 dark:to-rose-950/20 border border-red-200 dark:border-red-900/40 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3 text-center sm:text-left">
                <span className="text-3xl sm:text-4xl flex-shrink-0">❤️</span>
                <div>
                  <h3 className="font-bold text-red-900 dark:text-red-200 text-sm sm:text-base">
                    आप भी बनें रक्तवीर — "मैं भी रक्तवीर"
                  </h3>
                  <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
                    फतेहाबाद में रक्तदान के लिए खुद को रजिस्टर करें और अपना डिजिटल रक्तवीर कार्ड पाएं।
                  </p>
                </div>
              </div>
              <button
                onClick={openRegisterModal}
                className="flex-shrink-0 w-full sm:w-auto px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs sm:text-sm transition-all shadow active:scale-95"
              >
                🩸 Register Now
              </button>
            </div>
          )}

          {/* ── Blood Group Filter Chips (Swipeable on Mobile) ── */}
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Select Blood Group
              </p>
              {selectedGroup && (
                <button
                  onClick={() => { setSelectedGroup(''); setCurrentPage(1); }}
                  className="text-xs font-semibold text-red-600 dark:text-red-400 hover:underline"
                >
                  ✕ Clear ({selectedGroup})
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-none">
              <button
                onClick={() => { setSelectedGroup(''); setCurrentPage(1); }}
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-bold border transition-all active:scale-95 ${
                  !selectedGroup
                    ? 'bg-red-600 text-white border-red-600 shadow-sm'
                    : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                All Groups
              </button>
              {BLOOD_GROUPS.map(group => {
                const colors = BLOOD_GROUP_COLORS[group];
                const isActive = selectedGroup === group;
                return (
                  <button
                    key={group}
                    onClick={() => handleGroupFilter(group)}
                    style={isActive ? { backgroundColor: colors.text, color: '#fff', borderColor: colors.text } : { backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
                    className="flex-shrink-0 px-3.5 py-1.5 rounded-full border-2 font-black text-xs sm:text-sm transition-all hover:scale-105 active:scale-95 shadow-sm"
                  >
                    {group}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Search & Area Filters (Mobile Friendly) ── */}
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {/* Search Input */}
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search donor by name..."
                className="w-full pl-9 pr-8 py-2.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-red-400 shadow-sm"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600 px-1"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Area Dropdown */}
            <div className="relative">
              <select
                value={selectedArea}
                onChange={e => { setSelectedArea(e.target.value); setCurrentPage(1); }}
                className="w-full pl-9 pr-8 py-2.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-red-400 shadow-sm appearance-none cursor-pointer"
              >
                <option value="">All Areas in Fatehabad</option>
                {FATEHABAD_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">📍</span>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xs">▼</span>
            </div>
          </div>

          {/* ── Filter Counter & Quick Reset ── */}
          <div className="flex items-center justify-between mb-4 px-1 text-xs text-gray-500 dark:text-gray-400">
            <span>
              Showing <strong className="text-gray-900 dark:text-white">{displayedDonors.length}</strong> {displayedDonors.length === 1 ? 'donor' : 'donors'}
              {selectedGroup && <span className="font-semibold text-red-600 dark:text-red-400"> · {selectedGroup}</span>}
              {selectedArea && <span> · {selectedArea}</span>}
            </span>
            {(selectedGroup || selectedArea || searchQuery) && (
              <button
                onClick={() => { setSelectedGroup(''); setSelectedArea(''); setSearchQuery(''); setCurrentPage(1); }}
                className="text-red-600 dark:text-red-400 font-bold hover:underline"
              >
                Reset Filters
              </button>
            )}
          </div>

          {/* ── Not logged in notice (Compact on Mobile) ── */}
          {!loggedIn && (
            <div className="mb-4 flex items-center gap-3 p-3 sm:p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl">
              <span className="text-xl flex-shrink-0">🔒</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-bold text-amber-900 dark:text-amber-200">Phone numbers hidden for privacy</p>
                <p className="text-[11px] sm:text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                  <Link to="/login" state={{ from: '/blood-donors' }} className="underline font-bold">Login</Link> or <Link to="/signup" state={{ from: '/blood-donors' }} className="underline font-bold">Sign up</Link> to view contact numbers and call donors directly.
                </p>
              </div>
            </div>
          )}

          {/* ── Donor List ── */}
          {loading ? (
            <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-20 sm:h-24 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : displayedDonors.length === 0 ? (
            <div className="text-center py-12 sm:py-16 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6">
              <div className="text-5xl sm:text-6xl mb-3">🩸</div>
              <p className="text-gray-700 dark:text-gray-300 text-base sm:text-lg font-bold">No donors found</p>
              <p className="text-gray-400 dark:text-gray-500 text-xs sm:text-sm mt-1">
                {selectedGroup || selectedArea || searchQuery ? 'Try clearing or changing your filters.' : 'Be the first hero to register as a donor!'}
              </p>
              <button
                onClick={openRegisterModal}
                className="mt-5 inline-flex items-center gap-2 px-6 py-2.5 sm:py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all text-xs sm:text-sm active:scale-95 shadow-md"
              >
                🩸 Register as First Donor
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
              {displayedDonors.map(donor => (
                <DonorCard
                  key={donor._id}
                  donor={donor}
                  isLoggedIn={isLoggedInServer}
                />
              ))}
            </div>
          )}

          {/* ── Pagination ── */}
          {pagination && pagination.pages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                ← Prev
              </button>
              <span className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                Page {currentPage} of {pagination.pages}
              </span>
              <button
                disabled={currentPage === pagination.pages}
                onClick={() => handlePageChange(currentPage + 1)}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Register / Edit Modal (Mobile Bottom-Sheet) ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <div
            className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-gradient-to-r from-red-600 to-rose-700 px-5 sm:px-6 py-4 flex items-center justify-between shadow-sm">
              <h2 className="text-white font-bold text-base sm:text-lg flex items-center gap-2">
                <span>🩸</span>
                <span>{editMode ? 'Update Donor Details' : 'Register as Blood Donor'}</span>
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-base"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4">
              {/* Photo Upload / Avatar */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                  Donor Photo (Optional)
                </label>
                <div className="flex items-center gap-3.5">
                  <div className="relative flex-shrink-0">
                    {formData.image ? (
                      <img
                        src={formData.image}
                        alt="Preview"
                        className="w-16 h-16 rounded-2xl object-cover border-2 border-red-500 shadow-sm"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-2xl bg-red-50 dark:bg-gray-800 border-2 border-dashed border-red-300 dark:border-gray-600 flex flex-col items-center justify-center text-red-400 dark:text-gray-400">
                        <span className="text-xl">📷</span>
                        <span className="text-[9px] font-bold mt-0.5">Add Photo</span>
                      </div>
                    )}
                    {formData.image && (
                      <button
                        type="button"
                        onClick={() => setFormData(p => ({ ...p, image: '' }))}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-600 text-white rounded-full text-xs flex items-center justify-center shadow"
                        title="Remove photo"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="flex-1">
                    <label className="inline-flex items-center gap-2 px-3.5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-xl text-xs font-bold cursor-pointer transition-colors border border-gray-200 dark:border-gray-700">
                      <span>📸</span>
                      <span>{formData.image ? 'Change Photo' : 'Upload from Phone / Gallery'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file);
                        }}
                      />
                    </label>
                    <p className="text-[11px] text-gray-400 mt-1">
                      This photo will appear on your "मैं भी रक्तवीर" certificate card.
                    </p>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Your full name"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-400 text-sm"
                />
              </div>

              {/* Blood Group Grid */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                  Blood Group <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {BLOOD_GROUPS.map(g => {
                    const c = BLOOD_GROUP_COLORS[g];
                    const active = formData.bloodGroup === g;
                    return (
                      <button
                        type="button"
                        key={g}
                        onClick={() => setFormData(p => ({ ...p, bloodGroup: g }))}
                        style={active ? { backgroundColor: c.text, color: '#fff', borderColor: c.text } : { backgroundColor: c.bg, color: c.text, borderColor: c.border }}
                        className={`py-2 rounded-xl border-2 font-black text-sm transition-all active:scale-95 shadow-sm ${
                          active ? 'scale-105 ring-2 ring-red-400' : ''
                        }`}
                      >
                        {g}
                      </button>
                    );
                  })}
                </div>
                {!formData.bloodGroup && (
                  <p className="text-[11px] text-red-500 mt-1">Please tap your blood group</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                  Contact Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                  placeholder="e.g. 9876543210"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-400 text-sm font-mono"
                />
                <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                  <span>🔒</span> Number is protected and only visible to logged-in users
                </p>
              </div>

              {/* Area */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                  Area / Block in Fatehabad <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.area}
                  onChange={e => setFormData(p => ({ ...p, area: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-400 text-sm cursor-pointer"
                >
                  <option value="">Select area</option>
                  {FATEHABAD_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              {/* Availability (Edit Mode) */}
              {editMode && (
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-700">
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                    {formData.isAvailable ? '✅ Ready & Available to Donate' : '⛔ Currently Unavailable'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFormData(p => ({ ...p, isAvailable: !p.isAvailable }))}
                    className={`relative w-12 h-6 rounded-full transition-colors ${formData.isAvailable ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.isAvailable ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !formData.bloodGroup}
                className="w-full py-3.5 bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-700 hover:to-rose-800 text-white font-bold rounded-xl transition-all disabled:opacity-50 active:scale-95 shadow-lg text-sm sm:text-base mt-2"
              >
                {submitting ? 'Saving...' : editMode ? 'Save Updated Details' : '🩸 Register as Donor'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// ── Donor Card Component (Mobile Optimized) ──
const DonorCard = ({ donor, isLoggedIn }) => {
  const colors = BLOOD_GROUP_COLORS[donor.bloodGroup] || BLOOD_GROUP_COLORS['O+'];
  const available = donor.isAvailable !== false;
  const rawPhone = donor.phone ? donor.phone.replace(/\D/g, '').slice(-10) : '';

  return (
    <div className="relative p-3.5 sm:p-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      {/* Top Right Green Dot for Available Donors */}
      {available && (
        <span
          className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 flex h-2.5 w-2.5"
          title="Available to donate blood"
        >
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 ring-2 ring-white dark:ring-gray-900" />
        </span>
      )}

      {/* Top / Left Section */}
      <div className="flex items-center gap-3 min-w-0 pr-4 sm:pr-0">
        {/* Blood Group Badge / Donor Photo */}
        {donor.image ? (
          <div className="relative flex-shrink-0">
            <img
              src={donor.image}
              alt={donor.name}
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl object-cover border-2 border-white dark:border-gray-800 shadow-sm ring-1 ring-red-400/40"
            />
            <span
              className="absolute -bottom-1 -right-1 px-1.5 py-0.2 rounded-md font-black text-[10px] border shadow-xs"
              style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
            >
              {donor.bloodGroup}
            </span>
          </div>
        ) : (
          <div
            className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center font-black text-base sm:text-lg border-2 shadow-sm"
            style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
          >
            {donor.bloodGroup}
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-gray-900 dark:text-white text-sm sm:text-base truncate">
              {donor.name}
            </h3>
          </div>
          <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            <span>📍</span>
            <span className="truncate font-medium">{donor.area}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons (Full width on mobile, sleek inline on desktop) */}
      <div className="flex items-center gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100 dark:border-gray-800">
        {isLoggedIn ? (
          <>
            <a
              href={`tel:${donor.phone}`}
              onClick={() => trackPhoneView({
                targetType: 'blood_donor',
                targetId: donor._id,
                targetName: `${donor.name} (${donor.bloodGroup})`,
                targetCategory: donor.area || 'Fatehabad',
                viewedPhone: donor.phone,
                action: 'call_click'
              })}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs sm:text-sm font-bold transition-all active:scale-95 shadow-sm"
            >
              <span>📞</span>
              <span>{donor.phone}</span>
            </a>
            {rawPhone && (
              <a
                href={`https://wa.me/91${rawPhone}?text=${encodeURIComponent(`Hello ${donor.name}, I found your contact on Digital Fatehabad Blood Donors list. We urgently need ${donor.bloodGroup} blood donation.`)}`}
                onClick={() => trackPhoneView({
                  targetType: 'blood_donor',
                  targetId: donor._id,
                  targetName: `${donor.name} (${donor.bloodGroup})`,
                  targetCategory: donor.area || 'Fatehabad',
                  viewedPhone: donor.phone,
                  action: 'whatsapp_click'
                })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 rounded-xl text-xs sm:text-sm font-bold transition-all active:scale-95"
                title="Message on WhatsApp"
              >
                <span>💬</span>
                <span className="sm:hidden text-xs">WhatsApp</span>
                <span className="hidden sm:inline">WhatsApp</span>
              </a>
            )}
          </>
        ) : (
          <Link
            to="/login"
            state={{ from: '/blood-donors' }}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl text-xs sm:text-sm font-bold transition-all hover:bg-red-100 dark:hover:bg-red-900/40 active:scale-95"
          >
            <span>🔒</span>
            <span>Login to Call / View Number</span>
          </Link>
        )}
      </div>
    </div>
  );
};

export default BloodDonors;
